/**
 * Custom hook for managing Admin Settings state and operations
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { platformSettingsAPI, preferencesAPI, type SiteRegionalSettings, type PreferencesChoices, type GroupRegionalDefaults, type GroupRegionalDefaultsUpdate, type PurchasingTabsVisibility, type QualityTabsVisibility } from '../../../lib/api';
import { useMenuSettings, DEFAULT_MENU_VISIBILITY } from '../../../contexts/MenuSettingsContext';
import { useBranding } from '../../../contexts/BrandingContext';
import { useAuth } from '../../../contexts/AuthContext';
import {
  ThemeSettings,
  PlatformSettings,
  BarrattFlags,
  DEFAULT_THEME_SETTINGS,
  DEFAULT_BARRATT_FLAGS,
  DEFAULT_DASHBOARD_TABS,
  DEFAULT_SUPPLIER_DASHBOARD_TABS,
  DEFAULT_SUPPLIER_CARD_FIELDS,
  DEFAULT_DATA_IMPORT_TILES,
  DashboardTabsVisibility,
  SupplierCardFieldsVisibility,
  DataImportTilesVisibility,
  DEFAULT_WRITEBACK_RULES,
  WritebackRules,
} from './types';

export function useAdminSettings() {
  const { menuVisibility: contextMenuVisibility, updateMenuVisibility, loadSettingsFromBackend, updateDashboardTabs, updateSupplierDashboardTabs, updateSupplierCardFields, updateDataImportTiles } = useMenuSettings();
  const { loadCompanyBranding, resetColorsOnly } = useBranding();
  const { user } = useAuth();
  // Mirrors the backend's WRITEBACK_SETTING_KEYS guard.
  const canManageWriteback = Boolean(user?.is_superuser || user?.is_staff);

  // Theme settings state
  const [themeSettings, setThemeSettings] = useState<ThemeSettings>(DEFAULT_THEME_SETTINGS);

  // Platform settings state - Initialize with context menu visibility
  const [platformSettings, setPlatformSettings] = useState<PlatformSettings>(() => ({
    mainMenuType: '2',
    freeMode: false,
    mainCompanyCanLoginAs: false,
    defaultLandingSite: null,
    defaultLandingGroup: null,
    automationRelated: false,
    useDispositionInNCR: false,
    warehouseModule: false,
    turnEmailMAB: false,
    switchPartRepository: false,
    qualityMeasurePPK: false,
    writebackEnabled: true,
    writebackShowSapRow: false,
    writebackRulesEffective: DEFAULT_WRITEBACK_RULES,
    enable8dScoring: true,
    deliveryAppealBulkEnabled: false,
    notificationIconTop: true,
    notificationIconSide: false,
    ncrFaultClassificationOptional: false,
    restrictCountry: '',
    restrictCountryEnabled: false,
    menuVisibility: contextMenuVisibility,
    ncrTemplate: 1,
    // Upload Limits (Feature 5)
    maxUploadSizeMb: 50,
    allowedFileTypes: [],
    // Document Versioning (Feature 8)
    autoVersionDocuments: true,
    versionFormat: 'numeric',
    retainOldVersions: true,
    // Notification Defaults (Feature 3)
    defaultNotificationPreferences: {},
    systemAlertEmails: '',
    // Supplier Onboarding
    supplierOnboardingEnabled: true,
    // Hierarchy Labels
    hierarchyLevel1Label: 'Group',
    hierarchyLevel2Label: 'Division',
    hierarchyLevel3Label: 'Company',
    supplierDefaultLandingPath: '/dashboard',
    poLineSchedules: false,
  }));

  // File upload states
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: string]: boolean }>({});
  const [saving, setSaving] = useState(false);

  // Company regional settings state
  const [siteSettings, setSiteSettings] = useState<SiteRegionalSettings[]>([]);
  const [choices, setChoices] = useState<PreferencesChoices | null>(null);
  const [loadingSites, setLoadingSites] = useState(false);
  const [savingSite, setSavingSite] = useState<number | null>(null);
  const [editedSites, setEditedSites] = useState<{ [key: number]: Partial<SiteRegionalSettings> }>({});
  // CR-6256: group defaults shown at the top of Regional Settings, and the
  // site currently switching between inherit and override.
  const [groupDefaults, setGroupDefaults] = useState<GroupRegionalDefaults | null>(null);
  const [loadingGroupDefaults, setLoadingGroupDefaults] = useState(false);
  const [togglingInheritance, setTogglingInheritance] = useState<number | null>(null);
  const [savingGroupDefaults, setSavingGroupDefaults] = useState(false);
  const [siteSettingsError, setSiteSettingsError] = useState<string | null>(null);

  // Barratt feature flags state
  const [barrattFlags, setBarrattFlags] = useState<BarrattFlags>(DEFAULT_BARRATT_FLAGS);
  // CR-6160: the raw feature_flags dict as loaded from the backend. A platform
  // settings save rebuilds feature_flags from the managed subset (barrattFlags +
  // po_line_schedules); spreading this first preserves any sibling flag not in
  // that subset (e.g. asn_produmax_export, schedule_collaboration) instead of
  // silently dropping it.
  const loadedFeatureFlagsRef = useRef<Record<string, unknown>>({});
  // Customer review 11 Sep (item 35): true once the platform settings have
  // been applied. Until then every section renders DEFAULT_* values, and a Save
  // would write those defaults over the tenant's real settings (e.g. switch the
  // Forecast Total row off) — savePlatformSettings refuses until loaded.
  const platformLoadedRef = useRef(false);

  // Dashboard tab visibility state (CR-4441)
  const [dashboardTabs, setDashboardTabs] = useState<DashboardTabsVisibility>(DEFAULT_DASHBOARD_TABS);
  const [supplierDashboardTabs, setSupplierDashboardTabs] = useState<DashboardTabsVisibility>(DEFAULT_SUPPLIER_DASHBOARD_TABS);

  // Purchasing & Quality tab visibility
  const [purchasingTabs, setPurchasingTabs] = useState<PurchasingTabsVisibility>({
    rfq: true, pricing: true, po_headers: true, po_lines: true,
    forecast: true, asn: true, deliveries: true, call_off: true,
  });
  const [qualityTabs, setQualityTabs] = useState<QualityTabsVisibility>({
    ncrs: true, concessions: true, approval_register: true,
  });

  // Supplier card field visibility
  const [supplierCardFields, setSupplierCardFields] = useState<SupplierCardFieldsVisibility>(DEFAULT_SUPPLIER_CARD_FIELDS);

  // Data Import tile visibility
  const [dataImportTiles, setDataImportTiles] = useState<DataImportTilesVisibility>(DEFAULT_DATA_IMPORT_TILES);

  // Sync platform settings menu visibility with context when context changes
  useEffect(() => {
    setPlatformSettings(prev => ({
      ...prev,
      menuVisibility: contextMenuVisibility,
    }));
  }, [contextMenuVisibility]);

  // Load settings from API
  useEffect(() => {
    loadSettings();
    loadSiteSettings();
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      // CR-6150: company branding is company-scoped and loads for any admin,
      // but the platform-settings call (GET /core/settings/platform/) is
      // Helix-only and 403s for a customer/group admin. Use allSettled so that
      // 403 doesn't reject the whole load and blank the Branding screen — the
      // platform-only fields just fall back to theme defaults.
      const [platformResult, brandingResult] = await Promise.allSettled([
        platformSettingsAPI.getSettings(),
        platformSettingsAPI.getCompanyBranding(),
      ]);

      // Branding itself failing is the real error worth surfacing.
      if (brandingResult.status === 'rejected') {
        throw brandingResult.reason;
      }
      const branding = brandingResult.value.data;

      // PR #635 review: only swallow the EXPECTED 403 from the Helix-only
      // platform-settings endpoint (a customer/group admin still loads their
      // Branding screen from company-branding). Any other platform-settings
      // failure (500, network, timeout) is a real outage — re-throw it so the
      // catch/toast below surfaces it instead of silently proceeding on
      // defaults and hiding the problem from a Helix admin.
      if (platformResult.status === 'rejected') {
        const platformErrorStatus = (
          platformResult.reason as { response?: { status?: number } }
        )?.response?.status;
        if (platformErrorStatus !== 403) {
          throw platformResult.reason;
        }
      }
      const settings = platformResult.status === 'fulfilled'
        ? platformResult.value.data
        : null;

      // Default colors for fallback
      const defaultColors = {
        backgroundColor: '#ffffff',
        loginColor: '#4f46e5',
        fontColor: '#333333',
        buttonColor: '#4f46e5',
      };

      // Update theme settings from company branding (per-company isolation).
      // Company branding always applies; the platform-only fields (themes,
      // emails, website) only overlay when the Helix-only settings loaded.
      setThemeSettings({
        ...DEFAULT_THEME_SETTINGS,
        companyName: branding.company_name || settings?.company_name || '',
        loginTitle: branding.login_title || '',
        // Use company branding colors, fall back to defaults (not platform settings)
        backgroundColor: branding.background_color || defaultColors.backgroundColor,
        loginColor: branding.login_color || defaultColors.loginColor,
        fontColor: branding.font_color || defaultColors.fontColor,
        buttonColor: branding.button_color || defaultColors.buttonColor,
        ...(settings ? {
          defaultTheme: settings.default_theme,
          availableThemes: settings.available_themes,
          website: settings.website,
          supportEmail: settings.support_email,
          salesEmail: settings.sales_email,
          noReplyEmail: settings.no_reply_email,
          contactEmail: (settings as unknown as Record<string, string>)['contact_email'] || '',
          companyAddress: (settings as unknown as Record<string, string>)['company_address'] || '',
          companyPhone: (settings as unknown as Record<string, string>)['company_phone'] || '',
          emailSubject: settings.email_subject,
          emailSignature: settings.email_signature,
        } : {}),
      });

      // Platform-level settings (menus, feature flags, tab visibility) are
      // Helix-only — skip applying them entirely when they didn't load for a
      // customer/group admin (their Branding screen has already populated).
      if (!settings) {
        return;
      }

      // Update platform settings
      setPlatformSettings({
        mainMenuType: settings.main_menu_type,
        freeMode: settings.free_mode,
        mainCompanyCanLoginAs: settings.main_company_can_login_as,
        // CR-5371: id of the Company users land on after login when no
        // current_site is set. null = follow existing default behaviour.
        defaultLandingSite: (settings as unknown as Record<string, number | null>)['default_landing_site'] ?? null,
        // CR-5371 rework: id of the CompanyGroup alternative — mutually
        // exclusive with defaultLandingSite.
        defaultLandingGroup: (settings as unknown as Record<string, number | null>)['default_landing_group'] ?? null,
        automationRelated: settings.automation_related,
        useDispositionInNCR: settings.use_disposition_in_ncr,
        warehouseModule: settings.warehouse_module,
        turnEmailMAB: settings.turn_email_mab,
        switchPartRepository: settings.switch_part_repository,
        qualityMeasurePPK: settings.quality_measure_ppk,
        writebackEnabled: (settings as unknown as Record<string, boolean>)['writeback_enabled'] ?? true,
        writebackShowSapRow: (settings as unknown as Record<string, boolean>)['writeback_show_sap_row'] ?? false,
        // Backend merges its shipped defaults into writeback_rules_effective;
        // fall back to the local mirror only if the field is missing entirely.
        writebackRulesEffective: {
          ...DEFAULT_WRITEBACK_RULES,
          ...((settings as unknown as Record<string, WritebackRules | undefined>)['writeback_rules_effective'] ?? {}),
        },
        enable8dScoring: settings.enable_8d_scoring ?? true,
        deliveryAppealBulkEnabled: (settings as unknown as Record<string, boolean>)['delivery_appeal_bulk_enabled'] ?? false,
        notificationIconTop: (settings as unknown as Record<string, boolean>)['notification_icon_top'] ?? true,
        notificationIconSide: (settings as unknown as Record<string, boolean>)['notification_icon_side'] ?? false,
        ncrFaultClassificationOptional: (settings as unknown as Record<string, boolean>)['ncr_fault_classification_optional'] ?? false,
        restrictCountry: settings.restrict_country,
        restrictCountryEnabled: settings.restrict_country_enabled,
        // Merge defaults so admin toggles reflect the intended default state
        // even when the backend's stored dict is missing newer keys
        // (e.g. partsMaster). Without this merge, a missing key renders the
        // Switch as unchecked and any save will omit that key, so the
        // sidebar silently falls back to its own default and stays visible.
        menuVisibility: { ...DEFAULT_MENU_VISIBILITY, ...settings.menu_visibility },
        ncrTemplate: ((settings as unknown as Record<string, number>)['ncr_template'] || 1) as 1 | 2,
        // Upload Limits (Feature 5)
        maxUploadSizeMb: settings.max_upload_size_mb ?? 50,
        allowedFileTypes: settings.allowed_file_types ?? [],
        // Document Versioning (Feature 8)
        autoVersionDocuments: settings.auto_version_documents ?? true,
        versionFormat: settings.version_format ?? 'numeric',
        retainOldVersions: settings.retain_old_versions ?? true,
        // Notification Defaults (Feature 3)
        defaultNotificationPreferences: settings.default_notification_preferences ?? {},
        systemAlertEmails: settings.system_alert_emails ?? '',
        // Supplier Onboarding
        supplierOnboardingEnabled: settings.supplier_onboarding_enabled ?? true,
        // Hierarchy Labels
        hierarchyLevel1Label: settings.hierarchy_level_1_label ?? 'Group',
        hierarchyLevel2Label: settings.hierarchy_level_2_label ?? 'Division',
        hierarchyLevel3Label: settings.hierarchy_level_3_label ?? 'Company',
        supplierDefaultLandingPath: settings.supplier_default_landing_path ?? '/dashboard',
        poLineSchedules: settings.feature_flags?.po_line_schedules ?? false,
      });

      // Also update context with loaded menu visibility (defaults merged)
      updateMenuVisibility({ ...DEFAULT_MENU_VISIBILITY, ...settings.menu_visibility });

      // Load dashboard tab visibility (CR-4441)
      if (settings.dashboard_tabs) {
        setDashboardTabs({
          ...DEFAULT_DASHBOARD_TABS,
          ...settings.dashboard_tabs,
        });
      }
      if (settings.supplier_dashboard_tabs) {
        setSupplierDashboardTabs({
          ...DEFAULT_SUPPLIER_DASHBOARD_TABS,
          ...settings.supplier_dashboard_tabs,
        });
      }
      // Load purchasing & quality tab visibility
      if (settings.purchasing_tabs) {
        setPurchasingTabs((prev: PurchasingTabsVisibility) => ({ ...prev, ...settings.purchasing_tabs }));
      }
      if (settings.quality_tabs) {
        setQualityTabs((prev: QualityTabsVisibility) => ({ ...prev, ...settings.quality_tabs }));
      }
      // Load supplier card field visibility
      if (settings.supplier_card_fields) {
        setSupplierCardFields({
          ...DEFAULT_SUPPLIER_CARD_FIELDS,
          ...settings.supplier_card_fields,
        });
      }
      // Load Data Import tile visibility
      if (settings.data_import_tiles) {
        setDataImportTiles({
          ...DEFAULT_DATA_IMPORT_TILES,
          ...settings.data_import_tiles,
        });
      }

      // Load Barratt feature flags
      if (settings.feature_flags) {
        // CR-6160: keep the full dict so unmanaged sibling flags survive a save.
        loadedFeatureFlagsRef.current = settings.feature_flags as Record<string, unknown>;
        platformLoadedRef.current = true;
        setBarrattFlags({
          delivery_appeals: settings.feature_flags.delivery_appeals ?? false,
          spend_leakage: settings.feature_flags.spend_leakage ?? false,
          bulk_upload: settings.feature_flags.bulk_upload ?? false,
          commodity_codes: settings.feature_flags.commodity_codes ?? false,
          supplier_uploads: settings.feature_flags.supplier_uploads ?? false,
          spend_dashboard: settings.feature_flags.spend_dashboard ?? false,
          division_scoring: settings.feature_flags.division_scoring ?? false,
          call_off_performance: settings.feature_flags.call_off_performance ?? false,
          // CR-6726: allow_supplier_decline is no longer read into the managed
          // subset — Admin Setup owns it. loadedFeatureFlagsRef still carries
          // it, so a Feature Settings save preserves whatever the customer set
          // instead of dropping it.
          show_forecast_total: settings.feature_flags.show_forecast_total ?? true,
          // CR-6160: paid ASN logistics-tracking module (default OFF).
          asn_logistics_tracking: settings.feature_flags.asn_logistics_tracking ?? false,
          forecast_weeks_only: settings.feature_flags.forecast_weeks_only ?? false,
          forecast_hide_supplier_part_no: settings.feature_flags.forecast_hide_supplier_part_no ?? false,
          forecast_description_from_master: settings.feature_flags.forecast_description_from_master ?? false,
          // Unset → the install's delivery grain, the server's default too.
          forecast_show_po_number: settings.feature_flags.forecast_show_po_number
            ?? (settings.feature_flags.po_line_schedules ?? false),
          // CR-5334: normalise the legacy 'barratt' value to 'spend_analytics'
          // on read so the selector shows the renamed option even before the
          // data migration has run for this tenant.
          portal_mode: (settings.feature_flags.portal_mode === 'barratt'
            ? 'spend_analytics'
            : settings.feature_flags.portal_mode) ?? 'standard',
        });
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    }
  }, [updateMenuVisibility]);

  const loadSiteSettings = useCallback(async () => {
    setLoadingSites(true);
    setLoadingGroupDefaults(true);
    setSiteSettingsError(null);
    try {
      // CR-6256: the group defaults come from the same scope as the site list,
      // so they are fetched together and always describe the sites shown.
      const [sitesRes, choicesRes, groupDefaultsRes] = await Promise.all([
        preferencesAPI.getSiteRegionalSettings(),
        preferencesAPI.getPreferencesChoices(),
        preferencesAPI.getGroupRegionalDefaults(),
      ]);
      setSiteSettings(Array.isArray(sitesRes.data) ? sitesRes.data : []);
      setChoices(choicesRes.data);
      setGroupDefaults(groupDefaultsRes.data);
    } catch (error) {
      console.error('Failed to load site settings:', error);
      // A toast alone fades and leaves the page showing "no group defaults" /
      // "no companies", which reads as configuration rather than failure.
      setSiteSettingsError('Could not load regional settings. Check your connection and try again.');
      toast.error('Failed to load company regional settings');
    } finally {
      setLoadingSites(false);
      setLoadingGroupDefaults(false);
    }
  }, []);

  const handleSiteSettingChange = useCallback((siteId: number, field: string, value: string | boolean | null) => {
    setEditedSites(prev => ({
      ...prev,
      [siteId]: {
        ...prev[siteId],
        [field]: value === '' ? null : value,
      },
    }));
  }, []);

  /**
   * CR-6256: set the group's own regional defaults.
   *
   * A null field clears the group value so it falls back to the parent group or
   * the platform default. Reloads afterwards because every inheriting site's
   * displayed values move with this.
   */
  const saveGroupDefaults = useCallback(async (values: GroupRegionalDefaultsUpdate) => {
    setSavingGroupDefaults(true);
    try {
      const res = await preferencesAPI.updateGroupRegionalDefaults(values);
      setGroupDefaults(res.data);
      toast.success('Group default preferences saved');
      loadSiteSettings();
    } catch (error) {
      console.error('Failed to save group defaults:', error);
      toast.error('Failed to save group default preferences');
    } finally {
      setSavingGroupDefaults(false);
    }
  }, [loadSiteSettings]);

  /**
   * CR-6256: switch a site between inheriting the group defaults and holding
   * its own. Persisted on click rather than behind the Save button — it is a
   * single deliberate choice, and leaving it unsaved would show a site as
   * inheriting when it still overrides.
   *
   * The response replaces the row rather than just flipping the flag locally:
   * moving to Override seeds the site's own columns with the values it was
   * inheriting, and the dropdowns that appear must open on those, not on
   * whatever the row was last configured with.
   *
   * Any pending dropdown edits for the site are discarded, since they describe
   * a state the user has just left.
   */
  const setSiteInheritance = useCallback(async (siteId: number, usesGroupDefaults: boolean) => {
    setTogglingInheritance(siteId);
    try {
      const res = await preferencesAPI.updateSiteRegionalSettings(siteId, {
        uses_group_regional_defaults: usesGroupDefaults,
      });
      setEditedSites(prev => {
        const { [siteId]: _discarded, ...rest } = prev;
        return rest;
      });
      setSiteSettings(prev => prev.map(site => (
        site.id === siteId
          ? { ...site, ...res.data, uses_group_regional_defaults: usesGroupDefaults }
          : site
      )));
      toast.success(
        usesGroupDefaults
          ? 'Site now inherits the group company defaults'
          : 'Site now overrides the group company defaults',
      );
    } catch (error) {
      console.error('Failed to update site inheritance:', error);
      toast.error('Failed to update site regional settings');
    } finally {
      setTogglingInheritance(null);
    }
  }, []);

  const saveSiteSettings = useCallback(async (siteId: number) => {
    const edits = editedSites[siteId];
    if (!edits) return;

    setSavingSite(siteId);
    try {
      await preferencesAPI.updateSiteRegionalSettings(siteId, edits);
      toast.success('Company regional settings saved successfully!');
      // Clear edits for this site
      setEditedSites(prev => {
        const { [siteId]: _, ...rest } = prev;
        return rest;
      });
      // Reload site settings
      loadSiteSettings();
    } catch (error) {
      console.error('Failed to save site settings:', error);
      toast.error('Failed to save company regional settings');
    } finally {
      setSavingSite(null);
    }
  }, [editedSites, loadSiteSettings]);

  const saveThemeSettings = useCallback(async () => {
    // Platform Name is required for display purposes.
    if (!themeSettings.companyName.trim()) {
      toast.error('Platform Name is required', {
        description: 'Enter a value in Main Company Details before saving.',
        duration: 4000,
      });
      return;
    }

    setSaving(true);
    try {
      // Save branding colors to company-specific settings (per-tenant isolation)
      const brandingPayload = {
        company_name: themeSettings.companyName.trim(),
        login_title: themeSettings.loginTitle,
        background_color: themeSettings.backgroundColor,
        login_color: themeSettings.loginColor,
        font_color: themeSettings.fontColor,
        button_color: themeSettings.buttonColor,
      };

      await platformSettingsAPI.patchCompanyBranding(brandingPayload);

      // Reload company branding to apply new colors across the platform
      await loadCompanyBranding();

      toast.success('Branding settings saved successfully!', {
        description: 'Your company branding has been updated.',
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to save branding settings:', error);
      toast.error('Failed to save branding settings', {
        description: 'Please try again or contact support.',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  }, [themeSettings, loadCompanyBranding]);

  const savePlatformSettings = useCallback(async () => {
    if (!platformLoadedRef.current) {
      toast.error('Settings are still loading — try again in a moment.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        main_menu_type: platformSettings.mainMenuType,
        free_mode: platformSettings.freeMode,
        main_company_can_login_as: platformSettings.mainCompanyCanLoginAs,
        // CR-5371 (site/group are mutually exclusive; both sent explicitly
        // so picking one on the frontend clears the other server-side)
        default_landing_site: platformSettings.defaultLandingSite,
        default_landing_group: platformSettings.defaultLandingGroup,
        automation_related: platformSettings.automationRelated,
        use_disposition_in_ncr: platformSettings.useDispositionInNCR,
        warehouse_module: platformSettings.warehouseModule,
        turn_email_mab: platformSettings.turnEmailMAB,
        switch_part_repository: platformSettings.switchPartRepository,
        quality_measure_ppk: platformSettings.qualityMeasurePPK,
        // Writeback keys are platform-staff-only and the backend 403s the whole
        // request if anyone else sends them. This one payload is shared by every
        // Platform Admin tab, so including them unconditionally would block a
        // non-staff Helix admin from saving Features, Platform Config, anything.
        ...(canManageWriteback
          ? {
              writeback_enabled: platformSettings.writebackEnabled,
              writeback_show_sap_row: platformSettings.writebackShowSapRow,
              // Sent whole; the backend validates the keys and stores only the
              // rules that differ from its defaults.
              writeback_rules: platformSettings.writebackRulesEffective,
            }
          : {}),
        enable_8d_scoring: platformSettings.enable8dScoring,
        delivery_appeal_bulk_enabled: platformSettings.deliveryAppealBulkEnabled,
        notification_icon_top: platformSettings.notificationIconTop,
        notification_icon_side: platformSettings.notificationIconSide,
        ncr_fault_classification_optional: platformSettings.ncrFaultClassificationOptional,
        restrict_country: platformSettings.restrictCountry,
        restrict_country_enabled: platformSettings.restrictCountryEnabled,
        menu_visibility: platformSettings.menuVisibility,
        ncr_template: platformSettings.ncrTemplate,
        // CR-6160: spread the originally-loaded flags first so unmanaged siblings
        // (asn_produmax_export, schedule_collaboration, …) aren't dropped; the
        // managed subset then overlays the current values.
        feature_flags: { ...loadedFeatureFlagsRef.current, ...barrattFlags, po_line_schedules: platformSettings.poLineSchedules },
        // Upload Limits (Feature 5)
        max_upload_size_mb: platformSettings.maxUploadSizeMb,
        allowed_file_types: platformSettings.allowedFileTypes,
        // Document Versioning (Feature 8)
        auto_version_documents: platformSettings.autoVersionDocuments,
        version_format: platformSettings.versionFormat,
        retain_old_versions: platformSettings.retainOldVersions,
        // Notification Defaults (Feature 3)
        default_notification_preferences: platformSettings.defaultNotificationPreferences,
        system_alert_emails: platformSettings.systemAlertEmails,
        // Supplier Onboarding
        supplier_onboarding_enabled: platformSettings.supplierOnboardingEnabled,
        // Dashboard Tab Visibility (CR-4441)
        dashboard_tabs: dashboardTabs,
        // Hierarchy Labels
        hierarchy_level_1_label: platformSettings.hierarchyLevel1Label,
        hierarchy_level_2_label: platformSettings.hierarchyLevel2Label,
        hierarchy_level_3_label: platformSettings.hierarchyLevel3Label,
        supplier_default_landing_path: platformSettings.supplierDefaultLandingPath,
      };

      await platformSettingsAPI.patchSettings(payload);

      // Also update context with new menu visibility
      updateMenuVisibility(platformSettings.menuVisibility);

      // Refresh all settings from backend
      await loadSettingsFromBackend();

      // Count enabled menu items
      const enabledCount = Object.values(platformSettings.menuVisibility).filter(v => v).length;

      toast.success('Platform settings saved successfully!', {
        description: `Menu visibility updated. ${enabledCount} menu items are now visible.`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save platform settings', {
        description: 'Please try again or contact support.',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  }, [platformSettings, barrattFlags, dashboardTabs, updateMenuVisibility, loadSettingsFromBackend, canManageWriteback]);

  // CR-4441: Dedicated save for dashboard tab visibility
  const saveDashboardTabs = useCallback(async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({
        dashboard_tabs: dashboardTabs,
        supplier_dashboard_tabs: supplierDashboardTabs,
      });

      updateDashboardTabs(dashboardTabs);
      updateSupplierDashboardTabs(supplierDashboardTabs);

      toast.success('Dashboard settings saved successfully!');
    } catch (error) {
      console.error('Failed to save dashboard settings:', error);
      toast.error('Failed to save dashboard settings');
    } finally {
      setSaving(false);
    }
  }, [dashboardTabs, supplierDashboardTabs, updateDashboardTabs, updateSupplierDashboardTabs]);

  const savePurchasingTabs = useCallback(async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ purchasing_tabs: purchasingTabs });
      const enabledCount = Object.values(purchasingTabs).filter(v => v).length;
      toast.success('Purchasing settings saved!', {
        description: `${enabledCount} purchasing tabs enabled.`,
      });
    } catch { toast.error('Failed to save purchasing settings'); }
    finally { setSaving(false); }
  }, [purchasingTabs]);

  const saveQualityTabs = useCallback(async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ quality_tabs: qualityTabs });
      const enabledCount = Object.values(qualityTabs).filter(v => v).length;
      toast.success('Quality settings saved!', {
        description: `${enabledCount} quality tabs enabled.`,
      });
    } catch { toast.error('Failed to save quality settings'); }
    finally { setSaving(false); }
  }, [qualityTabs]);

  const saveSupplierCardFields = useCallback(async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ supplier_card_fields: supplierCardFields });
      updateSupplierCardFields(supplierCardFields);
      const enabledCount = Object.values(supplierCardFields).filter(v => v).length;
      toast.success('Supplier card settings saved!', {
        description: `${enabledCount} card elements enabled.`,
      });
    } catch { toast.error('Failed to save supplier card settings'); }
    finally { setSaving(false); }
  }, [supplierCardFields, updateSupplierCardFields]);

  const saveDataImportTiles = useCallback(async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ data_import_tiles: dataImportTiles });
      updateDataImportTiles(dataImportTiles);
      const enabledCount = Object.values(dataImportTiles).filter(v => v).length;
      toast.success('Data Import tiles saved!', {
        description: `${enabledCount} tiles enabled.`,
      });
    } catch { toast.error('Failed to save Data Import tiles'); }
    finally { setSaving(false); }
  }, [dataImportTiles, updateDataImportTiles]);

  const handleFileUpload = useCallback((fileType: string) => {
    // Map fileType keys to accepted file types
    const acceptMap: Record<string, string> = {
      companyLogo: 'image/png,image/jpeg,image/svg+xml',
      companyDarkLogo: 'image/png,image/jpeg,image/svg+xml',
      headerLogo: 'image/png,image/jpeg,image/svg+xml',
      loginImage: 'image/png,image/jpeg,image/webp',
      termsConditions: 'application/pdf',
      privacyPolicy: 'application/pdf',
    };

    // Map fileType keys to backend field names
    const fieldMap: Record<string, string> = {
      companyLogo: 'company_logo',
      companyDarkLogo: 'company_dark_logo',
      headerLogo: 'header_logo',
      loginImage: 'login_image',
      termsConditions: 'terms_conditions',
      privacyPolicy: 'privacy_policy',
    };

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = acceptMap[fileType] || 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      setUploadingFiles(prev => ({ ...prev, [fileType]: true }));
      try {
        const formData = new FormData();
        formData.append(fieldMap[fileType] || fileType, file);

        // Use the patch endpoint with FormData for file upload
        await platformSettingsAPI.patchSettings(formData as unknown as Record<string, unknown>);
        toast.success(`${fileType} uploaded successfully!`);
        loadSettings();
      } catch (error) {
        console.error(`Failed to upload ${fileType}:`, error);
        toast.error(`Failed to upload ${fileType}`);
      } finally {
        setUploadingFiles(prev => ({ ...prev, [fileType]: false }));
      }
    };
    input.click();
  }, [loadSettings]);

  const resetColorsToDefault = useCallback(async () => {
    // Hardcoded default colors - these are the true defaults that should be used
    const defaultColors = {
      backgroundColor: '#ffffff',
      loginColor: '#4f46e5',
      fontColor: '#333333',
      buttonColor: '#4f46e5',
    };
    setThemeSettings(prev => ({
      ...prev,
      ...defaultColors,
    }));
    setSaving(true);
    try {
      // Save default colors to company branding (ensures they're stored, not inherited from another company)
      await platformSettingsAPI.patchCompanyBranding({
        background_color: defaultColors.backgroundColor,
        login_color: defaultColors.loginColor,
        font_color: defaultColors.fontColor,
        button_color: defaultColors.buttonColor,
      });

      // Apply default colors to CSS immediately (preserves company name)
      resetColorsOnly();

      toast.success('Colors reset to defaults!');
    } catch (error) {
      console.error('Failed to reset colors:', error);
      toast.error('Failed to reset colors');
    } finally {
      setSaving(false);
    }
  }, [resetColorsOnly]);

  return {
    // Theme state
    themeSettings,
    setThemeSettings,

    // Platform state
    platformSettings,
    setPlatformSettings,

    // Barratt flags
    barrattFlags,
    setBarrattFlags,

    // Dashboard tab visibility (CR-4441)
    dashboardTabs,
    setDashboardTabs,
    supplierDashboardTabs,
    setSupplierDashboardTabs,
    purchasingTabs,
    setPurchasingTabs,
    savePurchasingTabs,
    qualityTabs,
    setQualityTabs,
    saveQualityTabs,
    supplierCardFields,
    setSupplierCardFields,
    saveSupplierCardFields,
    dataImportTiles,
    setDataImportTiles,
    saveDataImportTiles,

    // Site settings state
    siteSettings,
    choices,
    loadingSites,
    savingSite,
    editedSites,
    // CR-6256: group defaults + inherit/override toggle
    groupDefaults,
    loadingGroupDefaults,
    togglingInheritance,
    siteSettingsError,
    savingGroupDefaults,

    // File upload
    uploadingFiles,
    saving,

    // Actions
    saveThemeSettings,
    savePlatformSettings,
    saveDashboardTabs,
    handleFileUpload,
    resetColorsToDefault,
    handleSiteSettingChange,
    saveSiteSettings,
    setSiteInheritance,
    saveGroupDefaults,
    loadSiteSettings,
  };
}
